import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import {
  Search,
  ChevronDown,
  RotateCcw,
  X,
} from 'lucide-react'
import type { CheckoFilterParams, EntityType, SmbCategory, TaxRegime } from '@/types'
import { searchActivities, searchLocations } from '@/lib/checkoData'

interface JobFiltersProps {
  filters: CheckoFilterParams
  onFiltersChange: (filters: CheckoFilterParams) => void
}

/* ─── Shared Styles (matching CompanyFilters.tsx) ─── */

const compactInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 8px',
  fontSize: 12,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-xs)',
  color: 'var(--text-primary)',
  outline: 'none',
  transition: 'border-color var(--duration-fast)',
}

/* ─── Accordion Section (from CompanyFilters) ─── */

interface AccordionSectionProps {
  title: string
  subtitle?: string
  defaultOpen?: boolean
  children: React.ReactNode
}

function AccordionSection({ title, subtitle, defaultOpen = false, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const contentRef = useRef<HTMLDivElement>(null)
  const [maxH, setMaxH] = useState<number | undefined>(defaultOpen ? undefined : 0)

  useEffect(() => {
    if (open) {
      const el = contentRef.current
      if (el) {
        setMaxH(el.scrollHeight)
        const timer = setTimeout(() => setMaxH(undefined), 280)
        return () => clearTimeout(timer)
      }
    } else {
      setMaxH(0)
    }
  }, [open])

  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 10px',
          background: 'none',
          border: 'none',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500,
          transition: 'color var(--duration-fast)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ChevronDown
            size={12}
            style={{
              color: open ? 'var(--accent)' : 'var(--text-muted)',
              transform: open ? 'none' : 'rotate(-90deg)',
              transition: 'transform var(--duration-fast)',
            }}
          />
          {title}
          {subtitle && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11, marginLeft: 2 }}>{subtitle}</span>}
        </span>
      </button>
      <div
        ref={contentRef}
        style={{
          overflow: 'hidden',
          maxHeight: maxH,
          transition: maxH === 0 ? 'max-height 220ms ease-out' : 'max-height 260ms ease-in',
        }}
      >
        <div style={{ padding: '2px 10px 10px' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

/* ─── Sub-components ─── */

function NumericInput({ placeholder, value, onChange }: {
  placeholder: string
  value: number | undefined
  onChange: (v: number | undefined) => void
}) {
  return (
    <input
      type="number"
      style={compactInputStyle}
      value={value ?? ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : undefined)}
      placeholder={placeholder}
    />
  )
}

function PillToggle({ label, active, onChange }: {
  label: string
  active: boolean | undefined
  onChange: (v: boolean | undefined) => void
}) {
  const isActive = active === true
  return (
    <button
      onClick={() => onChange(isActive ? undefined : true)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        fontSize: 11,
        fontWeight: 500,
        background: isActive ? 'var(--accent-subtle)' : 'transparent',
        border: `1px solid ${isActive ? 'var(--accent-border)' : 'var(--border-default)'}`,
        borderRadius: 'var(--radius-full)',
        color: isActive ? 'var(--accent-bright)' : 'var(--text-muted)',
        cursor: 'pointer',
        transition: 'all var(--duration-fast)',
        whiteSpace: 'nowrap' as const,
      }}
    >
      {label}
    </button>
  )
}

/* ─── Activity (OKVED) Autocomplete ─── */

function ActivityAutocomplete({ selectedIds, onChange }: {
  selectedIds: string[]
  onChange: (ids: string[]) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<{ id: string; name: string; code: string }[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length < 1) { setResults([]); return }
      setLoading(true)
      const found = await searchActivities(query, 30)
      setResults(found)
      setLoading(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const addActivity = (id: string) => {
    if (!selectedIds.includes(id)) {
      onChange([...selectedIds, id])
    }
    setQuery('')
    setOpen(false)
  }

  const removeActivity = (id: string) => {
    onChange(selectedIds.filter(i => i !== id))
  }

  return (
    <div ref={ref}>
      <input
        style={compactInputStyle}
        placeholder="Поиск по коду или названию ОКВЭД..."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => { if (query) setOpen(true) }}
      />
      {loading && <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 0' }}>Загрузка...</div>}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          maxHeight: 180,
          overflowY: 'auto',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-xs)',
          boxShadow: 'var(--shadow-md)',
          marginTop: 2,
        }}>
          {results.map(r => (
            <button
              key={r.id}
              onClick={() => addActivity(r.id)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                padding: '5px 8px',
                fontSize: 11,
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-subtle)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              <span style={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              <span style={{ color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>{r.code}</span>
            </button>
          ))}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 6 }}>
          {selectedIds.map(id => (
            <span key={id} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '2px 7px',
              fontSize: 10,
              fontWeight: 500,
              background: 'var(--accent-subtle)',
              border: '1px solid var(--accent-border)',
              borderRadius: 'var(--radius-full)',
              color: 'var(--accent-bright)',
            }}>
              {id}
              <button
                onClick={() => removeActivity(id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 12,
                  height: 12,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  borderRadius: '50%',
                }}
              >
                <X size={8} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Location Autocomplete ─── */

function LocationAutocomplete({ selectedIds, onChange }: {
  selectedIds: string[]
  onChange: (ids: string[]) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<{ id: string; name: string }[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length < 1) { setResults([]); return }
      setLoading(true)
      const found = await searchLocations(query, 30)
      setResults(found)
      setLoading(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const addLocation = (id: string) => {
    if (!selectedIds.includes(id)) {
      onChange([...selectedIds, id])
    }
    setQuery('')
    setOpen(false)
  }

  const removeLocation = (id: string) => {
    onChange(selectedIds.filter(i => i !== id))
  }

  return (
    <div ref={ref}>
      <input
        style={compactInputStyle}
        placeholder="Поиск региона или города..."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => { if (query) setOpen(true) }}
      />
      {loading && <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 0' }}>Загрузка...</div>}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          maxHeight: 180,
          overflowY: 'auto',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-xs)',
          boxShadow: 'var(--shadow-md)',
          marginTop: 2,
        }}>
          {results.map(r => (
            <button
              onClick={() => addLocation(r.id)}
              style={{
                display: 'block',
                width: '100%',
                padding: '5px 8px',
                fontSize: 11,
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-subtle)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 6 }}>
          {selectedIds.map(id => (
            <span key={id} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '2px 7px',
              fontSize: 10,
              fontWeight: 500,
              background: 'var(--accent-subtle)',
              border: '1px solid var(--accent-border)',
              borderRadius: 'var(--radius-full)',
              color: 'var(--accent-bright)',
            }}>
              {id}
              <button
                onClick={() => removeLocation(id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 12,
                  height: 12,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  borderRadius: '50%',
                }}
              >
                <X size={8} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Select Dropdown ─── */

function SelectInput({ value, options, onChange }: {
  value: number | string | undefined
  options: { value: number | string; label: string }[]
  onChange: (v: number | string | undefined) => void
}) {
  return (
    <select
      style={{ ...compactInputStyle, appearance: 'none', paddingRight: 24, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235f6b83' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
      value={value ?? ''}
      onChange={e => onChange(e.target.value ? (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)) : undefined)}
    >
      {options.map(opt => (
        <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
      ))}
    </select>
  )
}

/* ─── Default Filters ─── */

export const defaultCheckoFilters: CheckoFilterParams = {
  search: undefined,
  entity: undefined,
  active: true,
  activities: [],
  locations: [],
  date_from: undefined,
  date_to: undefined,
  smb: undefined,
  smb_code: undefined,
  employee: undefined,
  employee_min: undefined,
  employee_max: undefined,
  capital: undefined,
  capital_min: undefined,
  fs_2110: undefined,
  fs_2110_min: undefined,
  fs_2110_max: undefined,
  fs_2400: undefined,
  fs_2400_min: undefined,
  fs_2400_max: undefined,
  fs_1300: undefined,
  fs_1300_min: undefined,
  fs_1300_max: undefined,
  tax_treatment: undefined,
  tax_treatment_code: undefined,
  tax_amount: undefined,
  tax_amount_min: undefined,
  cn: undefined,
  cncs_min: undefined,
  cnsp_min: undefined,
  phones: undefined,
  emails: undefined,
  websites: undefined,
  licenses: undefined,
  trademarks: undefined,
  leasing: undefined,
}

/* ─── Main Component ─── */

export default function JobFilters({ filters, onFiltersChange }: JobFiltersProps) {
  const update = useCallback(
    (patch: Partial<CheckoFilterParams>) => {
      onFiltersChange({ ...filters, ...patch })
    },
    [filters, onFiltersChange]
  )

  const activeCount = useMemo(() => {
    let count = 0
    if (filters.search) count++
    if (filters.entity) count++
    if (filters.active === false) count++
    if (filters.activities?.length) count++
    if (filters.locations?.length) count++
    if (filters.date_from) count++
    if (filters.date_to) count++
    if (filters.smb) count++
    if (filters.employee) count++
    if (filters.capital) count++
    if (filters.fs_2110) count++
    if (filters.fs_2400) count++
    if (filters.fs_1300) count++
    if (filters.tax_treatment) count++
    if (filters.tax_amount) count++
    if (filters.cn) count++
    if (filters.phones) count++
    if (filters.emails) count++
    if (filters.websites) count++
    if (filters.licenses) count++
    if (filters.trademarks) count++
    if (filters.leasing) count++
    return count
  }, [filters])

  const resetFilters = useCallback(() => {
    onFiltersChange({ ...defaultCheckoFilters })
  }, [onFiltersChange])

  // Show entity-specific sections only for organizations
  const showOrgOnly = filters.entity !== 'entrepreneurs'

  return (
    <div>
      {/* Top Bar: Search + Entity Type + Active + Reset */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' as const }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            placeholder="ИНН, ОГРН, название или ФИО..."
            value={filters.search ?? ''}
            onChange={e => update({ search: e.target.value || undefined })}
            style={{
              ...compactInputStyle,
              paddingLeft: 28,
              height: 34,
            }}
          />
        </div>

        {/* Entity type pills */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 'var(--radius-xs)', overflow: 'hidden', border: '1px solid var(--border-default)' }}>
          {([
            { key: undefined as EntityType | undefined, label: 'Все' },
            { key: 'organizations' as EntityType, label: 'Организации' },
            { key: 'entrepreneurs' as EntityType, label: 'ИП' },
          ] as const).map(opt => {
            const isAll = opt.key === undefined
            const isActive = isAll ? !filters.entity : filters.entity === opt.key
            return (
              <button
                key={String(opt.key ?? 'all')}
                onClick={() => update({ entity: isAll ? undefined : opt.key })}
                style={{
                  padding: '6px 10px',
                  fontSize: 11,
                  fontWeight: isActive ? 600 : 400,
                  background: isActive ? 'var(--accent-subtle)' : 'transparent',
                  color: isActive ? 'var(--accent-bright)' : 'var(--text-muted)',
                  border: 'none',
                  borderRight: opt.key !== 'entrepreneurs' ? '1px solid var(--border-subtle)' : 'none',
                  cursor: 'pointer',
                  transition: 'all var(--duration-fast)',
                  whiteSpace: 'nowrap' as const,
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>

        {/* Active-only toggle */}
        <button
          onClick={() => update({ active: !filters.active })}
          title="Действующие"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '5px 10px',
            fontSize: 11,
            fontWeight: 500,
            background: 'transparent',
            border: `1px solid ${filters.active !== false ? 'var(--success-border)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-full)',
            color: filters.active !== false ? 'var(--success)' : 'var(--text-muted)',
            cursor: 'pointer',
            transition: 'all var(--duration-fast)',
            whiteSpace: 'nowrap' as const,
          }}
        >
          <span style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: filters.active !== false ? 'var(--success)' : 'var(--text-muted)',
            transition: 'background var(--duration-fast)',
          }} />
          Действующие
        </button>

        {/* Reset */}
        {activeCount > 0 && (
          <button
            onClick={resetFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              fontSize: 11,
              fontWeight: 500,
              background: 'var(--bg-input)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-xs)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all var(--duration-fast)',
              whiteSpace: 'nowrap' as const,
            }}
          >
            <RotateCcw size={11} />
            Сброс
          </button>
        )}
      </div>

      {/* Accordion sections */}
      <div className="card" style={{ padding: 0, marginBottom: 16 }}>

        {/* ОКВЭД */}
        <AccordionSection title="Виды деятельности (ОКВЭД)" subtitle={filters.activities?.length ? `${filters.activities.length} выбр.` : ''} defaultOpen={!!filters.activities?.length}>
          <ActivityAutocomplete
            selectedIds={filters.activities ?? []}
            onChange={ids => update({ activities: ids.length ? ids : undefined })}
          />
        </AccordionSection>

        {/* Регионы */}
        <AccordionSection title="Регионы и города" subtitle={filters.locations?.length ? `${filters.locations.length} выбр.` : ''} defaultOpen={!!filters.locations?.length}>
          <LocationAutocomplete
            selectedIds={filters.locations ?? []}
            onChange={ids => update({ locations: ids.length ? ids : undefined })}
          />
        </AccordionSection>

        {/* Дата регистрации */}
        <AccordionSection title="Дата регистрации" defaultOpen={!!(filters.date_from || filters.date_to)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <input
              type="date"
              style={compactInputStyle}
              value={filters.date_from ?? ''}
              onChange={e => update({ date_from: e.target.value || undefined })}
              placeholder="От"
            />
            <input
              type="date"
              style={compactInputStyle}
              value={filters.date_to ?? ''}
              onChange={e => update({ date_to: e.target.value || undefined })}
              placeholder="До"
            />
          </div>
        </AccordionSection>

        {/* Реестр МСП */}
        <AccordionSection title="Реестр МСП" defaultOpen={!!filters.smb}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <PillToggle label="Входит в реестр МСП" active={filters.smb} onChange={v => update({ smb: v })} />
            </div>
            {filters.smb && (
              <SelectInput
                value={filters.smb_code ?? 0}
                options={[
                  { value: 0, label: 'Все типы' },
                  { value: 1, label: 'Микропредприятия' },
                  { value: 2, label: 'Малые предприятия' },
                  { value: 3, label: 'Средние предприятия' },
                ]}
                onChange={v => update({ smb_code: v as SmbCategory })}
              />
            )}
          </div>
        </AccordionSection>

        {/* Среднесписочная численность */}
        <AccordionSection title="Среднесписочная численность" defaultOpen={!!filters.employee}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <PillToggle label="Есть данные по ССЧ" active={filters.employee} onChange={v => update({ employee: v })} />
            {filters.employee && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <NumericInput placeholder="От, чел." value={filters.employee_min} onChange={v => update({ employee_min: v })} />
                <NumericInput placeholder="До, чел." value={filters.employee_max} onChange={v => update({ employee_max: v })} />
              </div>
            )}
          </div>
        </AccordionSection>

        {/* Уставный капитал (organizations only) */}
        {showOrgOnly && (
          <AccordionSection title="Уставный капитал" defaultOpen={!!filters.capital}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <PillToggle label="Имеется уставный капитал" active={filters.capital} onChange={v => update({ capital: v })} />
              {filters.capital && (
                <NumericInput placeholder="От, руб." value={filters.capital_min} onChange={v => update({ capital_min: v })} />
              )}
            </div>
          </AccordionSection>
        )}

        {/* Финансовая отчётность (organizations only) */}
        {showOrgOnly && (
          <AccordionSection title="Финансовая отчётность" defaultOpen={!!(filters.fs_2110 || filters.fs_2400 || filters.fs_1300)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Выручка */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <PillToggle label="Выручка за 2025 год" active={filters.fs_2110} onChange={v => update({ fs_2110: v })} />
                {filters.fs_2110 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <NumericInput placeholder="Выручка от, руб." value={filters.fs_2110_min} onChange={v => update({ fs_2110_min: v })} />
                    <NumericInput placeholder="Выручка до, руб." value={filters.fs_2110_max} onChange={v => update({ fs_2110_max: v })} />
                  </div>
                )}
              </div>
              {/* Чистая прибыль */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <PillToggle label="Чистая прибыль за 2025 г." active={filters.fs_2400} onChange={v => update({ fs_2400: v })} />
                {filters.fs_2400 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <NumericInput placeholder="Прибыль от, руб." value={filters.fs_2400_min} onChange={v => update({ fs_2400_min: v })} />
                    <NumericInput placeholder="Прибыль до, руб." value={filters.fs_2400_max} onChange={v => update({ fs_2400_max: v })} />
                  </div>
                )}
              </div>
              {/* Капитал */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <PillToggle label="Капитал за 2025 г." active={filters.fs_1300} onChange={v => update({ fs_1300: v })} />
                {filters.fs_1300 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <NumericInput placeholder="Капитал от, руб." value={filters.fs_1300_min} onChange={v => update({ fs_1300_min: v })} />
                    <NumericInput placeholder="Капитал до, руб." value={filters.fs_1300_max} onChange={v => update({ fs_1300_max: v })} />
                  </div>
                )}
              </div>
            </div>
          </AccordionSection>
        )}

        {/* Налоги и сборы */}
        <AccordionSection title="Налоги и сборы" defaultOpen={!!(filters.tax_treatment || filters.tax_amount)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Special tax regime */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <PillToggle label="Спец. налоговый режим" active={filters.tax_treatment} onChange={v => update({ tax_treatment: v })} />
              {filters.tax_treatment && (
                <SelectInput
                  value={filters.tax_treatment_code ?? 1}
                  options={[
                    { value: 1, label: 'УСН' },
                    { value: 2, label: 'АУСН' },
                    { value: 3, label: 'СРП' },
                    { value: 4, label: 'ЕСХН' },
                    { value: 0, label: 'Не используется' },
                  ]}
                  onChange={v => update({ tax_treatment_code: v as TaxRegime })}
                />
              )}
            </div>
            {/* Tax amount */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <PillToggle label="Данные об уплате налогов" active={filters.tax_amount} onChange={v => update({ tax_amount: v })} />
              {filters.tax_amount && (
                <NumericInput placeholder="Налоги от, руб." value={filters.tax_amount_min} onChange={v => update({ tax_amount_min: v })} />
              )}
            </div>
          </div>
        </AccordionSection>

        {/* Госзакупки */}
        <AccordionSection title="Участие в госзакупках" defaultOpen={!!filters.cn}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <PillToggle label="Участник госзакупок" active={filters.cn} onChange={v => update({ cn: v })} />
            {filters.cn && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <NumericInput placeholder="Заказчик, от руб." value={filters.cncs_min} onChange={v => update({ cncs_min: v })} />
                <NumericInput placeholder="Поставщик, от руб." value={filters.cnsp_min} onChange={v => update({ cnsp_min: v })} />
              </div>
            )}
          </div>
        </AccordionSection>

        {/* Контакты */}
        <AccordionSection title="Контакты" defaultOpen={!!(filters.phones || filters.emails || filters.websites)}>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4 }}>
            <PillToggle label="Телефон" active={filters.phones} onChange={v => update({ phones: v })} />
            <PillToggle label="Email" active={filters.emails} onChange={v => update({ emails: v })} />
            <PillToggle label="Веб-сайт" active={filters.websites} onChange={v => update({ websites: v })} />
          </div>
        </AccordionSection>

        {/* Дополнительно */}
        <AccordionSection title="Дополнительно" defaultOpen={!!(filters.licenses || filters.trademarks || filters.leasing)}>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4 }}>
            <PillToggle label="Лицензии" active={filters.licenses} onChange={v => update({ licenses: v })} />
            <PillToggle label="Товарные знаки" active={filters.trademarks} onChange={v => update({ trademarks: v })} />
            <PillToggle label="Лизинг" active={filters.leasing} onChange={v => update({ leasing: v })} />
          </div>
        </AccordionSection>
      </div>
    </div>
  )
}
